import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("");
  const [vkey, setVkey] = useState("");
  const [rkey, setRkey] = useState("");
  const [gkey, setGkey] = useState("");
  const [busy, setBusy] = useState(false);
  const [vbusy, setVbusy] = useState(false);
  const [rbusy, setRbusy] = useState(false);
  const [gbusy, setGbusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [vmsg, setVmsg] = useState("");
  const [rmsg, setRmsg] = useState("");
  const [gmsg, setGmsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, b] = await Promise.all([
        api.get("/settings"),
        api.get("/settings/deepseek-balance").catch(() => ({ data: { ok: false } })),
      ]);
      setSettings(s.data);
      setBalance(b.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!key || key.length < 10) { setMsg("Enter a valid key."); return; }
    setBusy(true); setMsg("");
    try {
      await api.put("/settings/deepseek-key", { api_key: key });
      setKey("");
      setMsg("Key inscribed.");
      await load();
    } catch (ex) {
      setMsg("Failed: " + (ex?.response?.data?.detail || "unknown"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your DeepSeek key? We'll fall back to the shared global key.")) return;
    setBusy(true); setMsg("");
    try { await api.delete("/settings/deepseek-key"); setMsg("Key removed."); await load(); }
    finally { setBusy(false); }
  };

  const saveVercel = async (e) => {
    e.preventDefault();
    if (!vkey || vkey.length < 10) { setVmsg("Paste a valid Vercel token."); return; }
    setVbusy(true); setVmsg("");
    try {
      const { data } = await api.put("/settings/vercel-token", { api_token: vkey });
      setVkey("");
      setVmsg(`✓ Connected as ${data.vercel_username || "user"}.`);
      await load();
    } catch (ex) {
      setVmsg("✗ " + (ex?.response?.data?.detail || "Failed"));
    } finally { setVbusy(false); }
  };

  const removeVercel = async () => {
    if (!window.confirm("Disconnect Vercel? You can reconnect anytime.")) return;
    setVbusy(true); setVmsg("");
    try { await api.delete("/settings/vercel-token"); setVmsg("Disconnected."); await load(); }
    finally { setVbusy(false); }
  };

  const saveRailway = async (e) => {
    e.preventDefault();
    if (!rkey || rkey.length < 10) { setRmsg("Paste a valid Railway token."); return; }
    setRbusy(true); setRmsg("");
    try {
      const { data } = await api.put("/settings/railway-token", { api_token: rkey });
      setRkey("");
      setRmsg(`✓ Connected as ${data.railway_username || "user"}.`);
      await load();
    } catch (ex) {
      setRmsg("✗ " + (ex?.response?.data?.detail || "Failed"));
    } finally { setRbusy(false); }
  };

  const removeRailway = async () => {
    if (!window.confirm("Disconnect Railway?")) return;
    setRbusy(true); setRmsg("");
    try { await api.delete("/settings/railway-token"); setRmsg("Disconnected."); await load(); }
    finally { setRbusy(false); }
  };

  const saveGithub = async (e) => {
    e.preventDefault();
    if (!gkey || gkey.length < 10) { setGmsg("Paste a valid GitHub token."); return; }
    setGbusy(true); setGmsg("");
    try {
      const { data } = await api.put("/settings/github-token", { api_token: gkey });
      setGkey("");
      setGmsg(`✓ Connected as ${data.github_username || "user"}.`);
      await load();
    } catch (ex) {
      setGmsg("✗ " + (ex?.response?.data?.detail || "Failed"));
    } finally { setGbusy(false); }
  };

  const removeGithub = async () => {
    if (!window.confirm("Disconnect GitHub?")) return;
    setGbusy(true); setGmsg("");
    try { await api.delete("/settings/github-token"); setGmsg("Disconnected."); await load(); }
    finally { setGbusy(false); }
  };

  return (
    <div data-testid="settings-page" style={{ padding: "60px 6vw 100px", maxWidth: 860, margin: "0 auto" }}>
      <div className="overline">§ Settings · The Scribe's Chamber</div>
      <h1 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 58px)", lineHeight: 1, margin: "14px 0 8px" }}>
        Bring <span className="font-display" style={{ color: "var(--brass)" }}>your own</span> key.
      </h1>
      <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 17, marginBottom: 40 }}>
        Route DeepSeek calls through your own account. Claude is served through the Emergent Universal Key and needs no configuration.
      </p>

      <div className="rule-parchment" style={{ marginBottom: 40 }} />

      {loading ? (
        <div className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)" }}>Loading...</div>
      ) : (
        <div style={{ display: "grid", gap: 28, gridTemplateColumns: "1fr 1fr" }}>
          {/* BYOK panel */}
          <div className="glass" style={{ padding: 32 }}>
            <div className="overline" style={{ marginBottom: 12 }}>DeepSeek API Key</div>
            <h3 className="font-serif" style={{ fontSize: 26, margin: "0 0 16px" }}>Per-user key</h3>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              <span
                data-testid="byok-status-badge"
                className="font-mono"
                style={{
                  fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
                  padding: "6px 10px", border: "1px solid",
                  color: settings?.deepseek_byok ? "var(--success)" : "var(--brass)",
                  borderColor: settings?.deepseek_byok ? "var(--success)" : "var(--brass)",
                }}
              >
                {settings?.deepseek_byok ? "BYOK ACTIVE" : "USING GLOBAL"}
              </span>
              {settings?.deepseek_key_masked && (
                <span data-testid="byok-masked-key" className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)" }}>
                  {settings.deepseek_key_masked}
                </span>
              )}
            </div>

            <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label className="label">New key</label>
              <input
                data-testid="byok-key-input"
                className="input-field"
                type="password"
                placeholder="sk-..."
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
                <button data-testid="byok-save-btn" className="btn-brass" disabled={busy}>
                  {busy ? "Inscribing..." : settings?.deepseek_byok ? "Update" : "Save Key"}
                </button>
                {settings?.deepseek_byok && (
                  <button
                    type="button" data-testid="byok-remove-btn"
                    className="btn-ghost" disabled={busy} onClick={remove}
                  >
                    Remove
                  </button>
                )}
              </div>
            </form>

            {msg && (
              <div data-testid="byok-message" className="font-mono" style={{ marginTop: 14, fontSize: 12, color: "var(--brass)" }}>
                ✦ {msg}
              </div>
            )}

            <div className="font-display" style={{ marginTop: 22, fontSize: 13, fontStyle: "italic", color: "var(--muted)", lineHeight: 1.55 }}>
              Obtain a key at{" "}
              <a className="link-brass" target="_blank" rel="noreferrer" href="https://platform.deepseek.com/api_keys">
                platform.deepseek.com/api_keys
              </a>
              . Kept encrypted at rest; used only when you select "DeepSeek" in any phase.
            </div>
          </div>

          {/* Balance panel */}
          <div className="glass" style={{ padding: 32 }}>
            <div className="overline" style={{ marginBottom: 12 }}>DeepSeek Balance</div>
            <h3 className="font-serif" style={{ fontSize: 26, margin: "0 0 16px" }}>Active key credit</h3>

            {balance?.ok ? (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
                  <span className="font-display" style={{ fontSize: 56, color: "var(--brass)", lineHeight: 1 }}>
                    {balance.total_balance}
                  </span>
                  <span className="font-mono" style={{ fontSize: 13, color: "var(--parchment-dim)", letterSpacing: "0.2em" }}>
                    {balance.currency}
                  </span>
                </div>
                <div data-testid="balance-source" className="font-mono" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--parchment-dim)" }}>
                  Source: {balance.source === "byok" ? "Your key" : "Shared global key"}
                </div>
                <div className="font-mono" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: balance.is_available ? "var(--success)" : "var(--warning)", marginTop: 6 }}>
                  {balance.is_available ? "✓ Available" : "⚠ Unavailable"}
                </div>
              </>
            ) : (
              <div data-testid="balance-error" className="font-mono" style={{ fontSize: 12, color: "var(--warning)", lineHeight: 1.6 }}>
                ⚠ Balance unavailable.
                <div style={{ marginTop: 8, color: "var(--parchment-dim)", fontSize: 11 }}>
                  {balance?.detail || "Provider did not respond."}
                </div>
              </div>
            )}

            <div className="rule-parchment" style={{ margin: "24px 0" }} />
            <button data-testid="balance-refresh-btn" className="btn-ghost" onClick={load} disabled={loading}>
              Refresh
            </button>
            <div className="font-display" style={{ marginTop: 22, fontSize: 13, fontStyle: "italic", color: "var(--muted)" }}>
              Top up at{" "}
              <a className="link-brass" target="_blank" rel="noreferrer" href="https://platform.deepseek.com/top_up">
                platform.deepseek.com/top_up
              </a>
              .
            </div>
          </div>

          {/* Vercel connect panel — spans both columns */}
          <div className="glass" style={{ padding: 32, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 14 }}>
              <div>
                <div className="overline" style={{ marginBottom: 8 }}>▲ Vercel · One-Click Deploy</div>
                <h3 className="font-serif" style={{ fontSize: 26, margin: "0 0 6px" }}>
                  Ship to production in one button.
                </h3>
                <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 15 }}>
                  Paste a Vercel personal access token. We upload your generated files directly to Vercel — no GitHub required.
                </p>
              </div>
              <span
                data-testid="vercel-status-badge"
                className="font-mono"
                style={{
                  fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
                  padding: "6px 10px", border: "1px solid",
                  color: settings?.vercel_connected ? "var(--success)" : "var(--parchment-dim)",
                  borderColor: settings?.vercel_connected ? "var(--success)" : "var(--parchment-dim)",
                  whiteSpace: "nowrap",
                }}
              >
                {settings?.vercel_connected ? "✓ CONNECTED" : "DISCONNECTED"}
              </span>
            </div>

            {settings?.vercel_connected && settings?.vercel_token_masked && (
              <div data-testid="vercel-masked-token" className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", marginBottom: 14 }}>
                Token: {settings.vercel_token_masked}
              </div>
            )}

            <form onSubmit={saveVercel} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 360px" }}>
                <label className="label">Vercel token</label>
                <input
                  data-testid="vercel-token-input"
                  className="input-field"
                  type="password"
                  placeholder="vercel_..."
                  value={vkey}
                  onChange={(e) => setVkey(e.target.value)}
                />
              </div>
              <button data-testid="vercel-save-btn" className="btn-brass" disabled={vbusy}>
                {vbusy ? "Validating..." : settings?.vercel_connected ? "Update Token" : "Connect Vercel"}
              </button>
              {settings?.vercel_connected && (
                <button type="button" data-testid="vercel-remove-btn" className="btn-ghost" disabled={vbusy} onClick={removeVercel}>
                  Disconnect
                </button>
              )}
            </form>

            {vmsg && (
              <div data-testid="vercel-message" className="font-mono" style={{ marginTop: 14, fontSize: 12, color: vmsg.startsWith("✗") ? "var(--warning)" : "var(--brass)" }}>
                {vmsg}
              </div>
            )}

            <div className="font-display" style={{ marginTop: 20, fontSize: 13, fontStyle: "italic", color: "var(--muted)", lineHeight: 1.55 }}>
              Get a token at{" "}
              <a className="link-brass" target="_blank" rel="noreferrer" href="https://vercel.com/account/tokens">
                vercel.com/account/tokens
              </a>
              . Scope: full access (needed to create deployments). Never shared; removable anytime.
            </div>
          </div>

          {/* Railway connect panel */}
          <div className="glass" style={{ padding: 32, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 14 }}>
              <div>
                <div className="overline" style={{ marginBottom: 8 }}>🚂 Railway · Project Auto-Create</div>
                <h3 className="font-serif" style={{ fontSize: 26, margin: "0 0 6px" }}>
                  Spin up a Railway project.
                </h3>
                <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 15 }}>
                  Paste a Railway API token. We create the project shell for your sprint via Railway GraphQL —
                  you connect the code source on Railway to finish.
                </p>
              </div>
              <span
                data-testid="railway-status-badge"
                className="font-mono"
                style={{
                  fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
                  padding: "6px 10px", border: "1px solid",
                  color: settings?.railway_connected ? "var(--success)" : "var(--parchment-dim)",
                  borderColor: settings?.railway_connected ? "var(--success)" : "var(--parchment-dim)",
                  whiteSpace: "nowrap",
                }}
              >
                {settings?.railway_connected ? "✓ CONNECTED" : "DISCONNECTED"}
              </span>
            </div>

            {settings?.railway_connected && settings?.railway_token_masked && (
              <div data-testid="railway-masked-token" className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", marginBottom: 14 }}>
                Token: {settings.railway_token_masked}
                {settings.railway_username && <span style={{ marginLeft: 12, color: "var(--brass)" }}>{settings.railway_username}</span>}
              </div>
            )}

            <form onSubmit={saveRailway} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 360px" }}>
                <label className="label">Railway token</label>
                <input
                  data-testid="railway-token-input"
                  className="input-field"
                  type="password"
                  placeholder="railway api token..."
                  value={rkey}
                  onChange={(e) => setRkey(e.target.value)}
                />
              </div>
              <button data-testid="railway-save-btn" className="btn-brass" disabled={rbusy}>
                {rbusy ? "Validating..." : settings?.railway_connected ? "Update Token" : "Connect Railway"}
              </button>
              {settings?.railway_connected && (
                <button type="button" data-testid="railway-remove-btn" className="btn-ghost" disabled={rbusy} onClick={removeRailway}>
                  Disconnect
                </button>
              )}
            </form>

            {rmsg && (
              <div data-testid="railway-message" className="font-mono" style={{ marginTop: 14, fontSize: 12, color: rmsg.startsWith("✗") ? "var(--warning)" : "var(--brass)" }}>
                {rmsg}
              </div>
            )}

            <div className="font-display" style={{ marginTop: 20, fontSize: 13, fontStyle: "italic", color: "var(--muted)", lineHeight: 1.55 }}>
              Get a token at{" "}
              <a className="link-brass" target="_blank" rel="noreferrer" href="https://railway.app/account/tokens">
                railway.app/account/tokens
              </a>
              . Requires read/write project scope.
            </div>
          </div>

          {/* GitHub connect panel */}
          <div className="glass" style={{ padding: 32, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 14 }}>
              <div>
                <div className="overline" style={{ marginBottom: 8 }}>⌘ GitHub · Push to Repository</div>
                <h3 className="font-serif" style={{ fontSize: 26, margin: "0 0 6px" }}>
                  Consecrate to a private repo.
                </h3>
                <p className="font-serif" style={{ color: "var(--parchment-dim)", fontStyle: "italic", fontSize: 15 }}>
                  Paste a GitHub PAT with <span className="font-mono" style={{ fontSize: 13 }}>repo</span> scope.
                  We create a private repository and inscribe every generated file — plus a HEXIS manifest — in a single commit.
                </p>
              </div>
              <span
                data-testid="github-status-badge"
                className="font-mono"
                style={{
                  fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
                  padding: "6px 10px", border: "1px solid",
                  color: settings?.github_connected ? "var(--success)" : "var(--parchment-dim)",
                  borderColor: settings?.github_connected ? "var(--success)" : "var(--parchment-dim)",
                  whiteSpace: "nowrap",
                }}
              >
                {settings?.github_connected ? "✓ CONNECTED" : "DISCONNECTED"}
              </span>
            </div>

            {settings?.github_connected && settings?.github_token_masked && (
              <div data-testid="github-masked-token" className="font-mono" style={{ fontSize: 12, color: "var(--parchment-dim)", marginBottom: 14 }}>
                Token: {settings.github_token_masked}
                {settings.github_username && <span style={{ marginLeft: 12, color: "var(--brass)" }}>@{settings.github_username}</span>}
                {settings.github_scopes?.length > 0 && (
                  <span style={{ marginLeft: 12, color: "var(--muted)" }}>
                    scopes: {settings.github_scopes.join(", ")}
                  </span>
                )}
              </div>
            )}

            <form onSubmit={saveGithub} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 360px" }}>
                <label className="label">GitHub PAT</label>
                <input
                  data-testid="github-token-input"
                  className="input-field"
                  type="password"
                  placeholder="ghp_... or github_pat_..."
                  value={gkey}
                  onChange={(e) => setGkey(e.target.value)}
                />
              </div>
              <button data-testid="github-save-btn" className="btn-brass" disabled={gbusy}>
                {gbusy ? "Validating..." : settings?.github_connected ? "Update Token" : "Connect GitHub"}
              </button>
              {settings?.github_connected && (
                <button type="button" data-testid="github-remove-btn" className="btn-ghost" disabled={gbusy} onClick={removeGithub}>
                  Disconnect
                </button>
              )}
            </form>

            {gmsg && (
              <div data-testid="github-message" className="font-mono" style={{ marginTop: 14, fontSize: 12, color: gmsg.startsWith("✗") ? "var(--warning)" : "var(--brass)" }}>
                {gmsg}
              </div>
            )}

            <div className="font-display" style={{ marginTop: 20, fontSize: 13, fontStyle: "italic", color: "var(--muted)", lineHeight: 1.55 }}>
              Get a token at{" "}
              <a className="link-brass" target="_blank" rel="noreferrer" href="https://github.com/settings/tokens/new?description=HEXIS%20OS&scopes=repo">
                github.com/settings/tokens/new
              </a>
              . Classic tokens need the <span className="font-mono" style={{ fontSize: 12 }}>repo</span> scope; fine-grained tokens need <span className="font-mono" style={{ fontSize: 12 }}>Contents: Read & write</span> + <span className="font-mono" style={{ fontSize: 12 }}>Administration: Read & write</span>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
